/**
 * CarrierCounterModelSection — the adversarial-reclassification workbench.
 *
 * The accountant-witness tool: drop in the CARRIER accountant's F/V/P
 * classification, reverse-engineer the number their methodology produces, and
 * work a ranked attack-vector table (methodology → rebuttal → status) for the
 * stand. Design spec: docs/plans/2026-07-30-adversarial-reclassification-ux.md;
 * API contract: docs/plans/2026-07-30-adversarial-reclassification-seam.md §6.
 *
 * MECHANISM (correct framing): reclassifying a category to "V" moves its
 * dollars into variable costs inside the variable-profit margin
 * (bi_engine.calculate_monthly_variable_profit), shrinking coverage-period VP
 * and the final claim. NOT a saved-cost deduction.
 *
 * NON-NEGOTIABLE FRAMING: everything here is a HYPOTHETICAL overlay of the
 * opponent's position. Our filed calculation is read-only, everywhere, always —
 * this component performs zero writes to any canonical store (the scenario
 * persists to its own carrier-counter-model document).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Swords,
  OctagonAlert,
  Check,
  Loader2,
  Printer,
  Save,
  RotateCcw,
  ShieldCheck,
  ShieldQuestion,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Coins,
  Receipt,
  TrendingDown,
  CalendarRange,
} from 'lucide-react';
import { getAuthHeaders } from '../../lib/authHeaders';
import {
  computeAdversarialReclassification,
  computeSavedCostsAttack,
  computePolicyWaterfallAttack,
  computeInsuredPercentageAttack,
  computeBenchmarkAttack,
  saveCarrierCounterModel,
  loadCarrierCounterModel,
  suggestDefenseStrategy,
  normalizeDefenseStrategies,
  type AdversarialReclassificationResponse,
  type AttackVector,
  type CarrierCounterModelDoc,
  type DefenseStrategy,
  type ExpenseClass,
  type SavedCostsAttackResponse,
  type SavedCostsRow,
  type PolicyWaterfallAttackResponse,
  type WaterfallGrossBasis,
  type InsuredPercentageAttackResponse,
  type BenchmarkAttackResponse,
  type VectorStatus,
} from '../../services/adversarialReclassificationApi';

// =============================================================================
// PROPS
// =============================================================================

interface CarrierCounterModelSectionProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
  /** report.financial_data (data_YYYY month arrays) — source of the grid rows.
   *  Categories come from the case's own expenses dict keys (seam §1). */
  financialData?: any;
  coverageYear?: number;
  /** Resolver-derived (GET /case-profile) default for the policy-waterfall
   *  gross-basis toggle. Optional so existing consumers (e.g.
   *  AccountantReportView) that don't pass it keep the 'bi' default. */
  initialBasis?: WaterfallGrossBasis;
}

// =============================================================================
// CLASSIFICATION SEED — mirrors backend bi_engine.EXPENSE_CATEGORIES
// (backend/app/services/bi_engine.py:240-346). DISPLAY BASELINE ONLY: after the
// first compute, the engine's `our_class` from the response overrides this seed
// (engine truth wins). Unknown categories default to "F", matching
// `self._classifications.get(category, "F")` in the engine.
// =============================================================================

const CLASS_SEED: Record<string, string> = {
  // Variable
  'bad debt expense': 'V', 'cogs - variable': 'V', 'cogs': 'V', 'cost of goods sold': 'V',
  'commissions': 'V', 'consumable goods': 'V', 'contract labor': 'V', 'credit card fees': 'V',
  'discounts & rebates': 'V', 'donations / contributions': 'V', 'drug testing': 'V',
  'franchise fees': 'V', 'freight': 'V', 'fuel expense': 'V', 'inventory adjustment': 'V',
  'repairs (excluding maintenance)': 'V', 'sales / lodging tax': 'V', 'training & education': 'V',
  'travel & entertainment': 'V', 'dues & subscriptions': 'V', 'dues and subscriptions': 'V',
  'childcare': 'V', 'housing allowances': 'V', 'housing allowance': 'V',
  'printing and reproduction': 'V', 'printing & reproduction': 'V', 'program expense': 'V',
  'program expenses': 'V', 'teaching materials': 'V', 'education materials': 'V',
  'meals': 'V', 'travel': 'V', 'variable_costs': 'V',
  // Semi-variable
  'auto expense': 'SV', 'automobile expense': 'SV', 'cleaning & housekeeping costs': 'SV',
  'janitorial expense': 'SV', 'janitorial': 'SV', 'computer & internet expenses': 'SV',
  'internet charges': 'SV', 'internet': 'SV', 'utilities': 'SV', 'total utilities': 'SV',
  'telephone': 'SV', 'phone': 'SV',
  // Fixed
  'advertising expense': 'F', 'bank charges': 'F', 'bank service charges': 'F',
  'cogs - fixed': 'F', 'contract services': 'F', 'fees': 'F', 'franchise fees - fixed': 'F',
  'insurance': 'F', 'total insurance': 'F', 'interest expense': 'F', 'lease expense': 'F',
  'license & taxes': 'F', 'taxes & licenses': 'F', 'maintenance': 'F', 'misc expense': 'F',
  'overhead': 'F', 'postage': 'F', 'postage and delivery': 'F', 'professional services': 'F',
  'professional fees': 'F', 'total professional fees': 'F', 'property taxes': 'F',
  'renovation expense': 'F', 'rental expense': 'F', 'rent': 'F', 'equipment rental': 'F',
  'retirement expense': 'F', 'security services': 'F', 'storage expense': 'F', 'supplies': 'F',
  'office supplies': 'F', 'uniforms': 'F', 'uncategorized expenses': 'F',
  'repairs & maintenance': 'F', 'total repairs & maintenance': 'F', 'repairs and maintenance': 'F',
  'fixed_costs': 'F',
  // Payroll
  'payroll': 'P', 'payroll expenses': 'P', 'officers payroll': 'P', 'officer compensation': 'P',
};

function seedClass(category: string): string {
  const key = category.trim().toLowerCase();
  if (CLASS_SEED[key]) return CLASS_SEED[key];
  if (key.includes('payroll') || key.includes('compensation')) return 'P';
  if (key.includes('cogs') || key.includes('commission') || key.includes('freight') || key.includes('fuel')) return 'V';
  return 'F';
}

// =============================================================================
// GRID ROW DERIVATION — rows come from the case's own expense categories (the
// same categories the VP engine consumes). The tool never invents categories.
// =============================================================================

interface GridRow {
  category: string;
  /** Coverage-year annual amount from the case's own monthly expenses. */
  ourAmount: number;
  /** Engine-truth (or seeded) class. May be "SV" from engine data. */
  ourClass: string;
  /** null = "same as ours" (the diff IS the input). */
  theirClass: ExpenseClass | null;
  /** Optional carrier figure override, raw input string. */
  theirAmountInput: string;
  /** True when ourAmount/ourClass came from the SavedCostsCalculator engine
   *  (our_detail[].actual / .classification) rather than the frontend's
   *  deriveGridRows sum + CLASS_SEED guess. Only engine-sourced rows may show
   *  the "Verified — from the filed calculation" mark. */
  ourAmountVerified?: boolean;
}

function deriveGridRows(financialData: any, coverageYear?: number): GridRow[] {
  if (!financialData || typeof financialData !== 'object') return [];
  const years = Object.keys(financialData)
    .filter((k) => k.startsWith('data_'))
    .map((k) => parseInt(k.replace('data_', ''), 10))
    .filter((y) => !Number.isNaN(y))
    .sort((a, b) => a - b);
  if (years.length === 0) return [];
  const amountYear = coverageYear && years.includes(coverageYear) ? coverageYear : years[years.length - 1];

  const totals: Record<string, number> = {};
  const allCategories = new Set<string>();
  for (const y of years) {
    const months = financialData[`data_${y}`];
    if (!Array.isArray(months)) continue;
    for (const m of months) {
      const expenses = m?.expenses;
      if (expenses && typeof expenses === 'object') {
        for (const [cat, amt] of Object.entries(expenses)) {
          allCategories.add(cat);
          if (y === amountYear && typeof amt === 'number') {
            totals[cat] = (totals[cat] || 0) + amt;
          }
        }
      }
    }
  }
  return Array.from(allCategories)
    .sort((a, b) => a.localeCompare(b))
    .map((category) => ({
      category,
      ourAmount: totals[category] || 0,
      ourClass: seedClass(category),
      theirClass: null,
      theirAmountInput: '',
      ourAmountVerified: false,
    }));
}

// =============================================================================
// SMALL PRESENTATIONAL HELPERS
// =============================================================================

/** Hueless class chip — our side, verified-gray ground state. */
function OurClassChip({ cls }: { cls: string }) {
  return (
    <span
      className="cs-chip inline-flex items-center justify-center font-mono"
      style={{
        minWidth: 26, padding: '2px 6px', borderRadius: 2,
        backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-hairline)',
        color: 'var(--color-verified)',
      }}
    >
      {cls}
    </span>
  );
}

/** Their class chip — contested red tint when the flip is an attack. */
function TheirClassChip({ cls, attack }: { cls: string; attack: boolean }) {
  return (
    <span
      className="cs-chip inline-flex items-center justify-center font-mono"
      style={{
        minWidth: 26, padding: '2px 6px', borderRadius: 2,
        // Carrier's divergent selection = crimson OUTLINE, no fill (K3 #4).
        backgroundColor: 'transparent',
        border: `1px solid ${attack ? 'var(--color-blocker)' : 'var(--color-hairline)'}`,
        color: attack ? 'var(--color-blocker)' : 'var(--color-verified)',
      }}
    >
      {cls}
    </span>
  );
}

const STATUS_CONFIG: Record<VectorStatus, { label: string; icon: React.ReactNode; style: React.CSSProperties }> = {
  countered: {
    label: 'Countered',
    icon: <ShieldCheck size={13} />,
    // Hueless — a defeated attack is the calm ground state.
    style: { color: 'var(--color-verified)', backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-hairline)' },
  },
  partial: {
    label: 'Partial',
    icon: <ShieldQuestion size={13} />,
    // Restrained needs-human marker — outline only, no fill (K3: orange fill is a double violation).
    style: { color: 'var(--color-needs-human)', backgroundColor: 'transparent', borderColor: 'var(--color-needs-human)' },
  },
  exposed: {
    label: 'Exposed',
    icon: <ShieldAlert size={13} />,
    // Outline-only, mirroring Partial — hue escalates the badge (gray→orange→crimson), never a fill (K3 confirm #1).
    style: { color: 'var(--color-blocker)', backgroundColor: 'transparent', borderColor: 'var(--color-blocker)' },
  },
};

interface VectorWork {
  rebuttalText: string;
  status: VectorStatus;
  suggesting: boolean;
  suggestions: DefenseStrategy[] | null;
  suggestError: string | null;
}

const emptyWork = (): VectorWork => ({
  rebuttalText: '',
  status: 'exposed',
  suggesting: false,
  suggestions: null,
  suggestError: null,
});

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// =============================================================================
// SAVED-COSTS OFFSET PANEL (v1.1 dimension #1)
//
// A SEPARATE carrier attack lever from reclassification: net loss = gross −
// saved, and the carrier enlarges `saved` by asserting the business's actual
// loss-period expenses were LOWER than our records. The witness drops in the
// carrier's asserted actuals per line; we recompute the offset their way and
// surface the delta. All dollar figures come from the backend SavedCostsCalculator
// (LLMs interpret, code calculates) — this panel computes nothing itself.
// =============================================================================

function SavedCostsAttackPanel({
  reportId,
  getAuthHeader,
  formatCurrency,
}: {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SavedCostsAttackResponse | null>(null);
  const [carrierInputs, setCarrierInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (carrier: Record<string, number> | null) => {
      setLoading(true);
      setErr(null);
      try {
        const resp = await computeSavedCostsAttack(reportId, carrier, await getAuthHeaders());
        setData(resp);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'Could not compute the saved-costs offset.');
      } finally {
        setLoading(false);
      }
    },
    [reportId, getAuthHeader],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!data) void load(null);
  }, [data, load]);

  const handleApplyCarrier = useCallback(() => {
    const parsed: Record<string, number> = {};
    for (const [k, v] of Object.entries(carrierInputs)) {
      const n = parseFloat(v);
      if (v.trim() !== '' && !Number.isNaN(n)) parsed[k] = n;
    }
    void load(Object.keys(parsed).length ? parsed : null);
  }, [carrierInputs, load]);

  const handleReset = useCallback(() => {
    setCarrierInputs({});
    void load(null);
  }, [load]);

  const rows = data?.our_detail ?? [];
  const theirByCat = useMemo(() => {
    const m: Record<string, number> = {};
    (data?.their_detail ?? []).forEach((r) => {
      m[r.category] = r.saved;
    });
    return m;
  }, [data]);
  const hasCarrier = (data?.their_detail ?? null) !== null;
  // delta > 0 means the carrier subtracts MORE than we do — adverse to the claim.
  const deltaAdverse = (data?.delta ?? 0) > 0.005;

  return (
    <div
      className="mt-5"
      data-testid="saved-costs-attack-panel"
      style={{
        border: '1px solid var(--color-border-sub)',
        borderRadius: 2,
        backgroundColor: 'var(--color-bg-elevated)',
      }}
    >
      <button
        onClick={() => (open ? setOpen(false) : handleOpen())}
        data-testid="saved-costs-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span className="flex items-center gap-2.5">
          <Coins className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          <span>
            <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Saved-costs offset
            </span>
            <span className="cs-meta block">
              &ldquo;You saved money by not operating.&rdquo; Net loss = gross &minus; saved.
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUp size={15} style={{ color: 'var(--color-text-ghost)' }} />
        ) : (
          <ChevronDown size={15} style={{ color: 'var(--color-text-ghost)' }} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && !data ? (
            <p className="cs-meta inline-flex items-center gap-1.5 py-2">
              <Loader2 size={12} className="animate-spin" /> Computing offset from the case&hellip;
            </p>
          ) : err ? (
            <p
              className="cs-body px-3 py-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}
            >
              {err}
            </p>
          ) : data ? (
            <>
              {/* Offset headline: our figure vs (when a carrier drop-in is applied) theirs. */}
              <div className="flex items-center gap-6 flex-wrap mb-3">
                <div>
                  <div className="cs-meta">Our saved-costs offset</div>
                  <div className="cs-figure" data-testid="saved-costs-our-total" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                    {formatCurrency(data.our_total_saved)}
                  </div>
                </div>
                {hasCarrier && (
                  <>
                    <div>
                      <div className="cs-meta">Carrier&apos;s asserted offset</div>
                      <div className="cs-figure" data-testid="saved-costs-their-total" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                        {formatCurrency(data.their_total_saved)}
                      </div>
                    </div>
                    <div>
                      <div className="cs-meta">Extra offset they&apos;d subtract</div>
                      <div
                        className="cs-figure"
                        data-testid="saved-costs-delta"
                        style={{ fontSize: '1.05rem', color: deltaAdverse ? 'var(--color-blocker)' : 'var(--color-verified)' }}
                      >
                        {formatCurrency(data.delta)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <p className="cs-meta mb-3" style={{ maxWidth: '46rem' }}>
                Projected is our baseline average; actual is the coverage-period figure. The carrier widens
                the offset by claiming a lower actual (more &ldquo;saved&rdquo;). Drop in their asserted
                actuals to see the offset they&apos;ll argue — the numbers stay computed by the engine.
              </p>

              {/* Per-line worksheet: projected / our actual / our saved / carrier actual drop-in. */}
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                      <th className="cs-meta text-left" style={{ padding: '6px 8px', fontWeight: 600 }}>Expense</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Projected</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Our actual</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Our saved</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Carrier actual</th>
                      {hasCarrier && <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Their saved</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.category} style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                        <td className="cs-body" style={{ padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
                          {r.category}
                        </td>
                        <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
                          {formatCurrency(r.projected)}
                        </td>
                        <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
                          {formatCurrency(r.actual)}
                        </td>
                        <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-primary)' }}>
                          {formatCurrency(r.saved)}
                        </td>
                        <td className="text-right" style={{ padding: '6px 8px' }}>
                          <input
                            type="number"
                            inputMode="decimal"
                            aria-label={`Carrier asserted actual for ${r.category}`}
                            value={carrierInputs[r.category] ?? ''}
                            placeholder={String(r.actual)}
                            onChange={(e) => setCarrierInputs((prev) => ({ ...prev, [r.category]: e.target.value }))}
                            className="cs-figure text-right"
                            style={{
                              width: 110, padding: '4px 6px', borderRadius: 2,
                              backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          />
                        </td>
                        {hasCarrier && (
                          <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-primary)' }}>
                            {theirByCat[r.category] !== undefined ? formatCurrency(theirByCat[r.category]) : '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={handleApplyCarrier}
                  disabled={loading}
                  data-testid="saved-costs-apply"
                  className="cs-meta inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-brand)',
                    backgroundColor: 'transparent', color: 'var(--color-brand)',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <Coins size={12} />}
                  Model their offset
                </button>
                {hasCarrier && (
                  <button
                    onClick={handleReset}
                    className="cs-meta inline-flex items-center gap-1.5"
                    style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-secondary)' }}
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// POLICY-DEDUCTION WATERFALL PANEL (v1.1 dimension #4)
//
// The carrier applies/maximizes deductible, coinsurance penalty, and policy
// limits to cut the net payout below the proven gross loss. The witness drops in
// the carrier's asserted policy terms; the same deterministic waterfall runs on
// our gross loss both ways. Applies to any gross loss — all figures come from the
// backend policy-deductions engine.
// =============================================================================

const POLICY_TERM_FIELDS: { key: string; label: string }[] = [
  { key: 'dollar_deductible', label: 'Dollar deductible' },
  { key: 'coinsurance_pct', label: 'Coinsurance %' },
  { key: 'actual_coverage_limit', label: 'Coverage limit' },
  { key: 'policy_limit', label: 'Policy limit' },
];

function PolicyWaterfallPanel({
  reportId,
  getAuthHeader,
  formatCurrency,
  initialBasis,
}: {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
  initialBasis?: WaterfallGrossBasis;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<PolicyWaterfallAttackResponse | null>(null);
  const [termInputs, setTermInputs] = useState<Record<string, string>>({});
  // Seeds the default from the resolver via the initializer (runs once at
  // mount); the user's later toggles via handleBasisChange are preserved.
  const [basis, setBasis] = useState<WaterfallGrossBasis>(initialBasis ?? 'bi');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (carrier: Record<string, number> | null, grossBasis: WaterfallGrossBasis) => {
      setLoading(true);
      setErr(null);
      try {
        const resp = await computePolicyWaterfallAttack(reportId, carrier, await getAuthHeaders(), grossBasis);
        setData(resp);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'Could not run the policy-deduction waterfall.');
      } finally {
        setLoading(false);
      }
    },
    [reportId, getAuthHeader],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!data) void load(null, basis);
  }, [data, load, basis]);

  const handleApply = useCallback(() => {
    const parsed: Record<string, number> = {};
    for (const [k, v] of Object.entries(termInputs)) {
      const n = parseFloat(v);
      if (v.trim() !== '' && !Number.isNaN(n)) parsed[k] = n;
    }
    void load(Object.keys(parsed).length ? parsed : null, basis);
  }, [termInputs, load, basis]);

  const handleReset = useCallback(() => {
    setTermInputs({});
    void load(null, basis);
  }, [load, basis]);

  const handleBasisChange = useCallback((next: WaterfallGrossBasis) => {
    setBasis(next);
    setTermInputs({});
    void load(null, next);
  }, [load]);

  const hasCarrier = (data?.their_waterfall ?? null) !== null;
  // The attack view shows the carrier's waterfall when applied, else ours.
  const shownWaterfall = hasCarrier ? data?.their_waterfall ?? [] : data?.our_waterfall ?? [];
  // delta < 0 means the carrier's terms cut MORE than ours — adverse to the claim.
  const deltaAdverse = (data?.delta ?? 0) < -0.005;

  return (
    <div
      className="mt-4"
      data-testid="policy-waterfall-panel"
      style={{
        border: '1px solid var(--color-border-sub)',
        borderRadius: 2,
        backgroundColor: 'var(--color-bg-elevated)',
      }}
    >
      <button
        onClick={() => (open ? setOpen(false) : handleOpen())}
        data-testid="policy-waterfall-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span className="flex items-center gap-2.5">
          <Receipt className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          <span>
            <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Policy-deduction waterfall
            </span>
            <span className="cs-meta block">
              Deductible, coinsurance, and limits the carrier applies to cut the net payout.
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUp size={15} style={{ color: 'var(--color-text-ghost)' }} />
        ) : (
          <ChevronDown size={15} style={{ color: 'var(--color-text-ghost)' }} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && !data ? (
            <p className="cs-meta inline-flex items-center gap-1.5 py-2">
              <Loader2 size={12} className="animate-spin" /> Running the waterfall on the case&hellip;
            </p>
          ) : err ? (
            <p
              className="cs-body px-3 py-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}
            >
              {err}
            </p>
          ) : data ? (
            <>
              {/* Gross basis: the BI variable-profit claim, or the property RCV. */}
              <div className="flex items-center gap-1.5 mb-3" role="group" aria-label="Gross basis">
                {([['bi', 'BI claim'], ['property', 'Property (RCV)'], ['fc', 'Forced closure']] as [WaterfallGrossBasis, string][]).map(([val, lbl]) => {
                  const active = basis === val;
                  return (
                    <button
                      key={val}
                      onClick={() => handleBasisChange(val)}
                      disabled={loading}
                      data-testid={`policy-waterfall-basis-${val}`}
                      aria-pressed={active}
                      className="cs-meta"
                      style={{
                        padding: '4px 10px', borderRadius: 2,
                        border: `1px solid ${active ? 'var(--color-border)' : 'var(--color-border-sub)'}`,
                        backgroundColor: active ? 'var(--color-bg-elevated)' : 'transparent',
                        color: active ? 'var(--color-text-primary)' : 'var(--color-text-ghost)',
                      }}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>

              {/* Headline: gross, our net (filed terms), their net (asserted terms), delta. */}
              <div className="flex items-center gap-6 flex-wrap mb-3">
                <div>
                  <div className="cs-meta">Gross loss</div>
                  <div className="cs-figure" data-testid="policy-waterfall-gross" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                    {formatCurrency(data.gross_loss)}
                  </div>
                </div>
                <div>
                  <div className="cs-meta">Our net (filed terms)</div>
                  <div className="cs-figure" data-testid="policy-waterfall-our-net" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                    {formatCurrency(data.our_net_claim)}
                  </div>
                </div>
                {hasCarrier && (
                  <>
                    <div>
                      <div className="cs-meta">Their net (asserted terms)</div>
                      <div className="cs-figure" data-testid="policy-waterfall-their-net" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                        {formatCurrency(data.their_net_claim)}
                      </div>
                    </div>
                    <div>
                      <div className="cs-meta">What their terms strip</div>
                      <div
                        className="cs-figure"
                        data-testid="policy-waterfall-delta"
                        style={{ fontSize: '1.05rem', color: deltaAdverse ? 'var(--color-blocker)' : 'var(--color-verified)' }}
                      >
                        {formatCurrency(data.delta)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Carrier policy-term drop-in. */}
              <div className="flex items-end gap-3 flex-wrap mb-3">
                {POLICY_TERM_FIELDS.map((f) => (
                  <label key={f.key} className="cs-meta" style={{ display: 'block' }}>
                    <span style={{ display: 'block', marginBottom: 2 }}>{f.label}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      aria-label={`Carrier asserted ${f.label}`}
                      value={termInputs[f.key] ?? ''}
                      placeholder={data.our_policy_terms?.[f.key] !== undefined ? String(data.our_policy_terms[f.key]) : '0'}
                      onChange={(e) => setTermInputs((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="cs-figure"
                      style={{
                        width: 120, padding: '5px 8px', borderRadius: 2,
                        backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                  </label>
                ))}
              </div>

              {/* Step-by-step waterfall (the carrier's when applied, else ours). */}
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                      <th className="cs-meta text-left" style={{ padding: '6px 8px', fontWeight: 600 }}>Step</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Deduction</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>Running balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownWaterfall.map((s, i) => (
                      <tr key={`${s.step}-${i}`} style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                        <td className="cs-body" style={{ padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
                          {s.description}
                        </td>
                        <td
                          className="cs-figure text-right"
                          style={{ padding: '6px 8px', color: s.deduction_amount > 0 ? 'var(--color-blocker)' : 'var(--color-text-ghost)' }}
                        >
                          {s.deduction_amount > 0 ? `(${formatCurrency(s.deduction_amount)})` : '—'}
                        </td>
                        <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-primary)' }}>
                          {formatCurrency(s.running_balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={handleApply}
                  disabled={loading}
                  data-testid="policy-waterfall-apply"
                  className="cs-meta inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-brand)',
                    backgroundColor: 'transparent', color: 'var(--color-brand)',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <Receipt size={12} />}
                  Model their terms
                </button>
                {hasCarrier && (
                  <button
                    onClick={handleReset}
                    className="cs-meta inline-flex items-center gap-1.5"
                    style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-secondary)' }}
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// INSURED-% PANEL (v1.1 dimension #2 — non-damage factor separation)
//
// The carrier argues a pre-existing declining revenue trend (market, not the
// peril) caused part of the shortfall, so only insured_percentage of the proven
// claim is compensable. The engine fits an OLS trend over baseline-year revenue;
// the carrier's lever is the baseline WINDOW. The witness toggles which baseline
// years the carrier would use; every percentage comes from the backend engine.
// =============================================================================

function InsuredPercentagePanel({
  reportId,
  getAuthHeader,
  formatCurrency,
}: {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<InsuredPercentageAttackResponse | null>(null);
  const [selectedYears, setSelectedYears] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (carrierYears: number[] | null) => {
      setLoading(true);
      setErr(null);
      try {
        const resp = await computeInsuredPercentageAttack(reportId, carrierYears, await getAuthHeaders());
        setData(resp);
        // Seed the toggles to the full baseline on first load.
        setSelectedYears((prev) => (prev === null ? resp.available_baseline_years : prev));
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'Could not run factor separation.');
      } finally {
        setLoading(false);
      }
    },
    [reportId, getAuthHeader],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!data) void load(null);
  }, [data, load]);

  const toggleYear = useCallback((year: number) => {
    setSelectedYears((prev) => {
      const base = prev ?? [];
      return base.includes(year) ? base.filter((y) => y !== year) : [...base, year].sort();
    });
  }, []);

  const handleApply = useCallback(() => {
    const years = selectedYears ?? [];
    const full = data?.available_baseline_years ?? [];
    // Only send when the carrier's window is a real subset (fewer years) — an
    // identical selection is our own baseline, so post null (our-side only).
    const isSubset = years.length > 0 && years.length < full.length;
    void load(isSubset ? years : null);
  }, [selectedYears, data, load]);

  const handleReset = useCallback(() => {
    setSelectedYears(data?.available_baseline_years ?? []);
    void load(null);
  }, [data, load]);

  const hasCarrier = (data?.carrier_baseline_years ?? null) !== null;
  const deltaAdverse = (data?.delta ?? 0) < -0.005;

  return (
    <div
      className="mt-4"
      data-testid="insured-pct-panel"
      style={{
        border: '1px solid var(--color-border-sub)',
        borderRadius: 2,
        backgroundColor: 'var(--color-bg-elevated)',
      }}
    >
      <button
        onClick={() => (open ? setOpen(false) : handleOpen())}
        data-testid="insured-pct-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span className="flex items-center gap-2.5">
          <TrendingDown className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          <span>
            <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Peril vs. market (insured %)
            </span>
            <span className="cs-meta block">
              &ldquo;A pre-existing decline caused part of it.&rdquo; Compensable = claim &times; insured %.
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUp size={15} style={{ color: 'var(--color-text-ghost)' }} />
        ) : (
          <ChevronDown size={15} style={{ color: 'var(--color-text-ghost)' }} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && !data ? (
            <p className="cs-meta inline-flex items-center gap-1.5 py-2">
              <Loader2 size={12} className="animate-spin" /> Fitting the baseline trend&hellip;
            </p>
          ) : err ? (
            <p
              className="cs-body px-3 py-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}
            >
              {err}
            </p>
          ) : data ? (
            <>
              <div className="flex items-center gap-6 flex-wrap mb-3">
                <div>
                  <div className="cs-meta">Our insured %</div>
                  <div className="cs-figure" data-testid="insured-pct-our" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                    {data.our_insured_pct.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="cs-meta">Our compensable</div>
                  <div className="cs-figure" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                    {formatCurrency(data.our_compensable)}
                  </div>
                </div>
                {hasCarrier && (
                  <>
                    <div>
                      <div className="cs-meta">Their insured %</div>
                      <div className="cs-figure" data-testid="insured-pct-their" style={{ fontSize: '1.05rem', color: 'var(--color-text-primary)' }}>
                        {data.their_insured_pct.toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <div className="cs-meta">What their trend strips</div>
                      <div
                        className="cs-figure"
                        data-testid="insured-pct-delta"
                        style={{ fontSize: '1.05rem', color: deltaAdverse ? 'var(--color-blocker)' : 'var(--color-verified)' }}
                      >
                        {formatCurrency(data.delta)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <p className="cs-meta mb-2" style={{ maxWidth: '46rem' }}>
                Untick the baseline years the carrier would exclude. Cherry-picking the declining years
                steepens the pre-existing trend and shifts loss off the peril — the engine recomputes the
                insured percentage.
              </p>

              {/* Baseline-year toggles — the carrier's drop-in window. */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {(data.available_baseline_years ?? []).map((year) => {
                  const active = (selectedYears ?? []).includes(year);
                  return (
                    <button
                      key={year}
                      onClick={() => toggleYear(year)}
                      data-testid={`insured-pct-year-${year}`}
                      aria-pressed={active}
                      className="cs-figure"
                      style={{
                        padding: '5px 12px', borderRadius: 2,
                        border: `1px solid ${active ? 'var(--color-border)' : 'var(--color-border-sub)'}`,
                        backgroundColor: active ? 'var(--color-bg-elevated)' : 'transparent',
                        color: active ? 'var(--color-text-primary)' : 'var(--color-text-ghost)',
                        textDecoration: active ? 'none' : 'line-through',
                      }}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>

              {hasCarrier && data.their_factors && data.their_factors.length > 0 && (
                <p className="cs-meta mb-3 px-3 py-2" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-border-sub)', borderRadius: 2, color: 'var(--color-text-secondary)' }}>
                  {data.their_factors[0]}
                </p>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={handleApply}
                  disabled={loading}
                  data-testid="insured-pct-apply"
                  className="cs-meta inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-brand)',
                    backgroundColor: 'transparent', color: 'var(--color-brand)',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <TrendingDown size={12} />}
                  Model their baseline
                </button>
                {hasCarrier && (
                  <button
                    onClick={handleReset}
                    className="cs-meta inline-flex items-center gap-1.5"
                    style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-secondary)' }}
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BENCHMARK PANEL (v1.1 dimension #3 — benchmark selection)
//
// calculate_claim picks the compensation-maximizing benchmark window; the carrier
// proposes a less-favorable window, lowering the claim. The witness picks the
// carrier's proposed window from the ranked candidate list; the claim under it is
// recomputed by the backend (forced-benchmark), never in JS.
// =============================================================================

function BenchmarkPanel({
  reportId,
  getAuthHeader,
  formatCurrency,
}: {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<BenchmarkAttackResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(
    async (carrierId: string | null) => {
      setLoading(true);
      setErr(null);
      try {
        const resp = await computeBenchmarkAttack(reportId, carrierId, await getAuthHeaders());
        setData(resp);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || 'Could not run the benchmark analysis.');
      } finally {
        setLoading(false);
      }
    },
    [reportId, getAuthHeader],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!data) void load(null);
  }, [data, load]);

  const handleApply = useCallback(() => {
    // Selecting our own optimal window is our-side only.
    const carrierId = selectedId && selectedId !== data?.our_benchmark_id ? selectedId : null;
    void load(carrierId);
  }, [selectedId, data, load]);

  const handleReset = useCallback(() => {
    setSelectedId(null);
    void load(null);
  }, [load]);

  const hasCarrier = (data?.their_benchmark_id ?? null) !== null;
  const deltaAdverse = (data?.delta ?? 0) < -0.005;

  return (
    <div
      className="mt-4"
      data-testid="benchmark-panel"
      style={{
        border: '1px solid var(--color-border-sub)',
        borderRadius: 2,
        backgroundColor: 'var(--color-bg-elevated)',
      }}
    >
      <button
        onClick={() => (open ? setOpen(false) : handleOpen())}
        data-testid="benchmark-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span className="flex items-center gap-2.5">
          <CalendarRange className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          <span>
            <span className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
              Benchmark selection
            </span>
            <span className="cs-meta block">
              &ldquo;Your benchmark period is cherry-picked.&rdquo; A weaker window lowers the claim.
            </span>
          </span>
        </span>
        {open ? (
          <ChevronUp size={15} style={{ color: 'var(--color-text-ghost)' }} />
        ) : (
          <ChevronDown size={15} style={{ color: 'var(--color-text-ghost)' }} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && !data ? (
            <p className="cs-meta inline-flex items-center gap-1.5 py-2">
              <Loader2 size={12} className="animate-spin" /> Ranking benchmark windows&hellip;
            </p>
          ) : err ? (
            <p
              className="cs-body px-3 py-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}
            >
              {err}
            </p>
          ) : data ? (
            <>
              <div className="flex items-center gap-6 flex-wrap mb-3">
                <div>
                  <div className="cs-meta">Our benchmark</div>
                  <div className="cs-figure" data-testid="benchmark-our-window" style={{ fontSize: '0.9rem', color: 'var(--color-text-primary)' }}>
                    {data.our_benchmark_label}
                  </div>
                  <div className="cs-figure" style={{ color: 'var(--color-text-secondary)' }}>{formatCurrency(data.our_claim)}</div>
                </div>
                {hasCarrier && (
                  <>
                    <div>
                      <div className="cs-meta">Their benchmark</div>
                      <div className="cs-figure" data-testid="benchmark-their-window" style={{ fontSize: '0.9rem', color: 'var(--color-text-primary)' }}>
                        {data.their_benchmark_label}
                      </div>
                      <div className="cs-figure" style={{ color: 'var(--color-text-secondary)' }}>{formatCurrency(data.their_claim)}</div>
                    </div>
                    <div>
                      <div className="cs-meta">What their window strips</div>
                      <div
                        className="cs-figure"
                        data-testid="benchmark-delta"
                        style={{ fontSize: '1.05rem', color: deltaAdverse ? 'var(--color-blocker)' : 'var(--color-verified)' }}
                      >
                        {formatCurrency(data.delta)}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <p className="cs-meta mb-2" style={{ maxWidth: '46rem' }}>
                Pick the window the carrier would argue for instead of our optimal one (showing the top{' '}
                {data.candidates.length} of {data.total_candidates} by favorability). A weaker benchmark
                shrinks the variable-profit lift — the engine recomputes the claim.
              </p>

              {/* Ranked candidate windows — the carrier's drop-in choice. */}
              <div className="overflow-x-auto mb-3">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                      <th className="cs-meta text-left" style={{ padding: '6px 8px', fontWeight: 600 }}>Window</th>
                      <th className="cs-meta text-left" style={{ padding: '6px 8px', fontWeight: 600 }}>Type</th>
                      <th className="cs-meta text-right" style={{ padding: '6px 8px', fontWeight: 600 }}>VP lift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.candidates.map((c) => {
                      const isOurs = c.id === data.our_benchmark_id;
                      const isSelected = c.id === (selectedId ?? data.our_benchmark_id);
                      return (
                        <tr
                          key={c.id}
                          data-testid={`benchmark-candidate-${c.id}`}
                          onClick={() => setSelectedId(c.id)}
                          style={{
                            borderBottom: '1px solid var(--color-border-sub)',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'var(--color-bg-elevated)' : 'transparent',
                          }}
                        >
                          <td className="cs-figure" style={{ padding: '6px 8px', color: isSelected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
                            {c.label}
                            {isOurs && <span className="cs-meta" style={{ marginLeft: 8, color: 'var(--color-verified)' }}>OURS</span>}
                          </td>
                          <td className="cs-meta" style={{ padding: '6px 8px', color: 'var(--color-text-ghost)' }}>{c.period_type}</td>
                          <td className="cs-figure text-right" style={{ padding: '6px 8px', color: 'var(--color-text-primary)' }}>
                            {formatCurrency(c.variance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleApply}
                  disabled={loading}
                  data-testid="benchmark-apply"
                  className="cs-meta inline-flex items-center gap-1.5"
                  style={{
                    padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-brand)',
                    backgroundColor: 'transparent', color: 'var(--color-brand)',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <CalendarRange size={12} />}
                  Model their benchmark
                </button>
                {hasCarrier && (
                  <button
                    onClick={handleReset}
                    className="cs-meta inline-flex items-center gap-1.5"
                    style={{ padding: '6px 12px', borderRadius: 2, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-secondary)' }}
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function CarrierCounterModelSection({
  reportId,
  getAuthHeader,
  formatCurrency,
  financialData,
  coverageYear,
  initialBasis,
}: CarrierCounterModelSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [rows, setRows] = useState<GridRow[]>(() => deriveGridRows(financialData, coverageYear));
  const [result, setResult] = useState<AdversarialReclassificationResponse | null>(null);
  const [work, setWork] = useState<Record<string, VectorWork>>({});
  const [computing, setComputing] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [expandedVector, setExpandedVector] = useState<string | null>(null);
  const [witnessMode, setWitnessMode] = useState(false);
  const [autoComputeQueued, setAutoComputeQueued] = useState(false);
  const [savedCostsForGrid, setSavedCostsForGrid] = useState<SavedCostsAttackResponse | null>(null);
  const loadedOnce = useRef(false);

  // Re-derive rows if financial data arrives/changes after mount.
  useEffect(() => {
    setRows((prev) => {
      const next = deriveGridRows(financialData, coverageYear);
      if (next.length === 0) return prev;
      // Preserve any user edits already made on matching categories, and keep
      // any engine-verified ourAmount (see savedCostsForGrid merge below) so a
      // financialData reference change doesn't silently fall back to the sum.
      return next.map((r) => {
        const existing = prev.find((p) => p.category === r.category);
        if (!existing) return r;
        return {
          ...r,
          ourAmount: existing.ourAmountVerified ? existing.ourAmount : r.ourAmount,
          ourClass: existing.ourClass,
          ourAmountVerified: existing.ourAmountVerified,
          theirClass: existing.theirClass,
          theirAmountInput: existing.theirAmountInput,
        };
      });
    });
  }, [financialData, coverageYear]);

  // Fetch the engine's coverage-year per-category actuals/classifications
  // (SavedCostsCalculator, via the saved-costs-attack endpoint the component
  // already imports computeSavedCostsAttack for) so the grid's "Our amount" /
  // "Ours" columns are engine truth, not a React-side monthly sum + seed guess.
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await computeSavedCostsAttack(reportId, null, await getAuthHeaders());
        if (!cancelled) setSavedCostsForGrid(resp);
      } catch {
        // Engine call failed — rows stay on the deriveGridRows fallback sum
        // (ourAmountVerified stays false, so no row claims "verified").
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Merge the engine's per-category actual/classification into the grid,
  // replacing the frontend sum + CLASS_SEED guess wherever the engine reported
  // on that category. Categories absent from our_detail keep the fallback.
  useEffect(() => {
    const detail = savedCostsForGrid?.our_detail;
    if (!detail || detail.length === 0) return;
    const byCategory: Record<string, SavedCostsRow> = {};
    for (const row of detail) byCategory[row.category] = row;
    setRows((prev) =>
      prev.map((r) => {
        const eng = byCategory[r.category];
        if (!eng) return r;
        return { ...r, ourAmount: eng.actual, ourClass: eng.classification, ourAmountVerified: true };
      }),
    );
  }, [savedCostsForGrid]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  const changedRows = useMemo(
    () => rows.filter((r) => r.theirClass !== null && r.theirClass !== r.ourClass),
    [rows],
  );
  const expenseOverrides = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of rows) {
      const v = parseFloat(r.theirAmountInput.replace(/[$,]/g, ''));
      if (r.theirAmountInput.trim() !== '' && Number.isFinite(v) && v !== r.ourAmount) out[r.category] = v;
    }
    return out;
  }, [rows]);
  const anyChanged = changedRows.length > 0 || Object.keys(expenseOverrides).length > 0;

  const classificationOverrides = useMemo(() => {
    const out: Record<string, ExpenseClass> = {};
    for (const r of changedRows) out[r.category] = r.theirClass as ExpenseClass;
    return out;
  }, [changedRows]);

  const footedSum = useMemo(
    () => (result ? result.attack_vectors.reduce((s, v) => s + v.loss_impact, 0) : 0),
    [result],
  );

  // ---------------------------------------------------------------------------
  // Compute (the read-only overlay recompute — never touches our calculation)
  // ---------------------------------------------------------------------------
  const handleCompute = useCallback(async () => {
    if (!anyChanged || computing) return;
    setComputing(true);
    setError(null);
    try {
      const body: { classification_overrides: Record<string, ExpenseClass>; expense_overrides?: Record<string, number> } = {
        classification_overrides: classificationOverrides,
      };
      if (Object.keys(expenseOverrides).length > 0) body.expense_overrides = expenseOverrides;
      const resp = await computeAdversarialReclassification(reportId, body, await getAuthHeaders());
      setResult(resp);
      setStale(false);
      // Engine truth wins: reconcile the grid's baseline class with the
      // response's our_class for every category the engine reported on.
      setRows((prev) =>
        prev.map((r) => {
          const v = resp.attack_vectors.find((av) => av.category === r.category);
          return v && v.our_class !== r.ourClass ? { ...r, ourClass: v.our_class } : r;
        }),
      );
      // Seed rebuttal work rows for any new vectors (keep existing authored work).
      setWork((prev) => {
        const next = { ...prev };
        for (const v of resp.attack_vectors) {
          if (!next[v.category]) next[v.category] = emptyWork();
        }
        return next;
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to compute the carrier model');
    } finally {
      setComputing(false);
    }
  }, [anyChanged, computing, classificationOverrides, expenseOverrides, reportId, getAuthHeader]);

  // ---------------------------------------------------------------------------
  // Load persisted scenario on mount; if one exists, restore + auto-compute so
  // the accountant re-opens the same model at deposition without re-entry.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    (async () => {
      try {
        const doc: CarrierCounterModelDoc | null = await loadCarrierCounterModel(reportId, await getAuthHeaders());
        if (!doc) return;
        setExpanded(true);
        setLabel(doc.label || '');
        setSavedAt(doc.saved_at || doc.updated_at || null);
        setRows((prev) =>
          prev.map((r) => {
            const cls = doc.classification_overrides[r.category];
            return cls ? { ...r, theirClass: cls } : r;
          }),
        );
        setWork((prev) => {
          const next = { ...prev };
          for (const v of doc.vectors || []) {
            next[v.category] = {
              ...emptyWork(),
              rebuttalText: v.rebuttal_text || '',
              status: v.status || 'exposed',
            };
          }
          return next;
        });
        setAutoComputeQueued(true);
      } catch {
        /* no saved model — normal first run */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    if (autoComputeQueued && anyChanged && !computing) {
      setAutoComputeQueued(false);
      handleCompute();
    }
  }, [autoComputeQueued, anyChanged, computing, handleCompute]);

  // ---------------------------------------------------------------------------
  // Row edits
  // ---------------------------------------------------------------------------
  const setTheirClass = (category: string, cls: ExpenseClass) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        // Clicking the button matching our class resets the row to "same as ours".
        const next: ExpenseClass | null = cls === r.ourClass ? null : cls;
        return { ...r, theirClass: r.theirClass === cls ? null : next };
      }),
    );
    if (result) setStale(true);
  };

  const cycleTheirClass = (category: string, dir: 1 | -1) => {
    const order: ExpenseClass[] = ['F', 'V', 'P'];
    setRows((prev) =>
      prev.map((r) => {
        if (r.category !== category) return r;
        const current = r.theirClass ?? (order.includes(r.ourClass as ExpenseClass) ? (r.ourClass as ExpenseClass) : 'F');
        const idx = (order.indexOf(current) + dir + order.length) % order.length;
        const next = order[idx];
        return { ...r, theirClass: next === r.ourClass ? null : next };
      }),
    );
    if (result) setStale(true);
  };

  const setTheirAmount = (category: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.category === category ? { ...r, theirAmountInput: value } : r)));
    if (result) setStale(true);
  };

  const handleReset = () => {
    const hasTyped = rows.some((r) => r.theirAmountInput.trim() !== '');
    if (hasTyped && !window.confirm('Reset to our classification? This discards the carrier figures you typed.')) return;
    setRows((prev) => prev.map((r) => ({ ...r, theirClass: null, theirAmountInput: '' })));
    setResult(null);
    setStale(false);
  };

  // ---------------------------------------------------------------------------
  // Rebuttal workbench
  // ---------------------------------------------------------------------------
  const updateWork = (category: string, patch: Partial<VectorWork>) => {
    setWork((prev) => ({ ...prev, [category]: { ...(prev[category] || emptyWork()), ...patch } }));
  };

  const handleSuggest = async (vector: AttackVector) => {
    updateWork(vector.category, { suggesting: true, suggestError: null });
    try {
      const resp = await suggestDefenseStrategy(
        reportId,
        { category: vector.category, their_class: vector.their_class },
        await getAuthHeaders(),
      );
      updateWork(vector.category, { suggesting: false, suggestions: normalizeDefenseStrategies(resp) });
    } catch (err: any) {
      updateWork(vector.category, {
        suggesting: false,
        suggestError: err?.response?.data?.detail || 'Failed to fetch a defense strategy',
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const vectors = (result?.attack_vectors || []).map((v) => ({
        category: v.category,
        rebuttal_text: work[v.category]?.rebuttalText || '',
        status: work[v.category]?.status || ('exposed' as VectorStatus),
      }));
      const resp = await saveCarrierCounterModel(
        reportId,
        { classification_overrides: classificationOverrides, vectors, ...(label.trim() ? { label: label.trim() } : {}) },
        await getAuthHeaders(),
      );
      setSavedAt(resp?.saved_at || new Date().toISOString());
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save the scenario');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Witness sheet — condensed print-safe export (counsel-side prep material;
  // deliberately NOT part of the court PDF bundle).
  // ---------------------------------------------------------------------------
  const handlePrint = () => {
    if (!result) return;
    // Standalone witness-sheet document — prints on paper, so it deliberately uses a
    // paper-ink palette outside the app's dark-theme token system (not app DOM).
    const pInk = '#111'; // design-law-allow: standalone print doc, not app DOM
    const pSub = '#555'; // design-law-allow: standalone print doc, not app DOM
    const pFaint = '#666'; // design-law-allow: standalone print doc, not app DOM
    const pRule = '#ccc'; // design-law-allow: standalone print doc, not app DOM
    const pRed = '#b91c1c'; // design-law-allow: standalone print doc, not app DOM
    const strip =
      `<div style="border:2px dashed ${pRed};color:${pRed};font-weight:700;padding:8px 12px;margin:12px 0;font-size:12px;letter-spacing:0.04em;">HYPOTHETICAL — CARRIER&#39;S MODEL, NOT OUR POSITION · WITNESS PREP ONLY · NOT A COURT EXHIBIT</div>`;
    const rowsHtml = result.attack_vectors
      .map((v, i) => {
        const w = work[v.category];
        const status = STATUS_CONFIG[w?.status || 'exposed'].label;
        const firstLine = (w?.rebuttalText || '').split('\n')[0] || '— no rebuttal prepared (OPEN EXPOSURE)';
        return `<tr>
          <td style="padding:6px 8px;font-family:monospace;">${i + 1}</td>
          <td style="padding:6px 8px;">${escapeHtml(v.category)}</td>
          <td style="padding:6px 8px;font-family:monospace;">${escapeHtml(String(v.our_class))} &rarr; ${escapeHtml(v.their_class)}</td>
          <td style="padding:6px 8px;text-align:right;font-family:monospace;">${v.loss_impact < 0 ? `(${escapeHtml(formatCurrency(Math.abs(v.loss_impact)))})` : escapeHtml(formatCurrency(v.loss_impact))}</td>
          <td style="padding:6px 8px;">${escapeHtml(status)}</td>
          <td style="padding:6px 8px;">${escapeHtml(firstLine)}</td>
        </tr>`;
      })
      .join('');
    const html = `<!doctype html><html><head><title>Witness Sheet — Carrier Counter-Model</title></head>
<body style="font-family:Georgia,serif;color:${pInk};margin:32px;font-size:13px;">
${strip}
<h1 style="font-size:18px;margin:0 0 4px;">Carrier Counter-Model — Witness Sheet</h1>
<p style="margin:0 0 16px;color:${pSub};">${escapeHtml(label || 'Unlabeled scenario')} · report ${escapeHtml(reportId)}</p>
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
  <tr>
    <td style="padding:8px;border:1px solid ${pRule};"><div style="font-size:10px;text-transform:uppercase;color:${pFaint};">Our net loss (filed)</div><div style="font-family:monospace;font-size:16px;">${escapeHtml(formatCurrency(result.our_net_loss))}</div></td>
    <td style="padding:8px;border:1px solid ${pRule};"><div style="font-size:10px;text-transform:uppercase;color:${pFaint};">Their net loss (carrier model)</div><div style="font-family:monospace;font-size:16px;">${escapeHtml(formatCurrency(result.their_net_loss))}</div></td>
    <td style="padding:8px;border:1px solid ${pRule};"><div style="font-size:10px;text-transform:uppercase;color:${pFaint};">The gap</div><div style="font-family:monospace;font-size:16px;color:${pRed};">(${escapeHtml(formatCurrency(Math.abs(result.delta)))})</div></td>
  </tr>
</table>
${result.reconciles === false ? `<p style="color:${pRed};font-size:11px;">Note: the attack table does not foot to the aggregate gap (claim-floor interaction).</p>` : ''}
<table style="width:100%;border-collapse:collapse;font-size:12px;">
  <thead><tr style="border-bottom:2px solid ${pInk};text-align:left;">
    <th style="padding:6px 8px;">#</th><th style="padding:6px 8px;">Category</th><th style="padding:6px 8px;">Reclass</th><th style="padding:6px 8px;text-align:right;">Impact</th><th style="padding:6px 8px;">Status</th><th style="padding:6px 8px;">Rebuttal (first line)</th>
  </tr></thead>
  <tbody>${rowsHtml}</tbody>
</table>
${strip}
<script>window.onload = function(){ window.print(); };</script>
</body></html>`;
    const win = window.open('', '_blank', 'width=940,height=1100');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  // ---------------------------------------------------------------------------
  // RENDER HELPERS
  // ---------------------------------------------------------------------------

  const hypotheticalChip = (
    <span
      data-testid="ccm-hypothetical-chip"
      className="cs-chip inline-flex items-center gap-1.5"
      style={{
        // K3 #2: no pill fill — a 2px crimson left-bar carries the adverse marker;
        // only the "HYPOTHETICAL —" lead word is crimson, the rest is hueless.
        padding: '4px 10px 4px 8px',
        backgroundColor: 'transparent',
        boxShadow: 'inset 2px 0 0 var(--color-blocker)',
        letterSpacing: '0.04em',
      }}
    >
      <OctagonAlert size={12} style={{ color: 'var(--color-blocker)' }} />
      <span style={{ color: 'var(--color-blocker)' }}>HYPOTHETICAL —</span>
      <span style={{ color: 'var(--color-text-secondary)' }}>CARRIER&apos;S MODEL, NOT OUR POSITION</span>
    </span>
  );

  const renderStatTiles = () => {
    const notRun = !result;
    const dim = computing || stale;
    const tile = (
      testId: string,
      labelText: string,
      body: React.ReactNode,
      chip: React.ReactNode,
    ) => (
      <div
        data-testid={testId}
        style={{
          backgroundColor: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-hairline)',
          borderRadius: 2,
          padding: 16,
          opacity: dim ? 0.55 : 1,
          transition: 'opacity 120ms ease-out',
        }}
      >
        <p className="cs-label mb-2">{labelText}</p>
        <div className="text-right">{body}</div>
        <div className="mt-2 flex justify-end">{chip}</div>
      </div>
    );

    const notRunFigure = (
      <span className="cs-chip" style={{ color: 'var(--color-text-ghost)', border: '1px solid var(--color-border-sub)', borderRadius: 2, padding: '2px 8px' }}>
        NOT RUN
      </span>
    );

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {tile(
          'ccm-tile-ours',
          'Our net loss',
          notRun ? notRunFigure : (
            <span className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(result!.our_net_loss)}
            </span>
          ),
          <span className="cs-chip inline-flex items-center gap-1" style={{ color: 'var(--color-verified)' }}>
            <Check size={12} /> FILED CALCULATION
          </span>,
        )}
        {tile(
          'ccm-tile-theirs',
          'Their net loss',
          notRun ? notRunFigure : (
            // Opacity tier carries trust: their figure sits one text tier down.
            <span className="cs-figure-lg" style={{ color: 'var(--color-text-secondary)' }}>
              {formatCurrency(result!.their_net_loss)}
            </span>
          ),
          <span
            className="cs-chip inline-flex items-center gap-1"
            style={{ color: 'var(--color-blocker)', backgroundColor: 'transparent', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, padding: '2px 8px' }}
          >
            CARRIER MODEL
          </span>,
        )}
        {tile(
          'ccm-tile-gap',
          'The gap',
          notRun ? notRunFigure : result!.delta === 0 ? (
            <span className="cs-figure-lg" style={{ color: 'var(--color-text-ghost)' }} title="Verified zero — their model matches ours">
              —
            </span>
          ) : (
            <span className="cs-figure-lg" style={{ color: 'var(--color-text-primary)' }}>
              {result!.delta < 0 ? `(${formatCurrency(Math.abs(result!.delta))})` : `+${formatCurrency(result!.delta)}`}
            </span>
          ),
          <span className="cs-meta">what their reclassification strips from the claim</span>,
        )}
      </div>
    );
  };

  const renderGrid = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <p className="cs-label">
          Carrier&apos;s classification overlay ·{' '}
          <span style={{ color: changedRows.length > 0 ? 'var(--color-blocker)' : 'var(--color-text-dim)' }}>
            {changedRows.length} of {rows.length}
          </span>{' '}
          <span style={{ color: 'var(--color-text-muted)' }}>categories reclassified</span>
        </p>
        <button
          onClick={handleReset}
          className="cs-meta inline-flex items-center gap-1.5"
          style={{ color: 'var(--color-text-muted)', padding: '4px 8px', borderRadius: 2, border: '1px solid var(--color-border-sub)', background: 'transparent' }}
        >
          <RotateCcw size={12} /> Reset to ours
        </button>
      </div>
      <div className="overflow-x-auto" style={{ border: '1px solid var(--color-border)', borderRadius: 2 }}>
        <table className="min-w-full" data-testid="ccm-overlay-grid">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <th className="cs-label px-3 py-2 text-left">Category</th>
              <th className="cs-label px-3 py-2 text-right">Our amount</th>
              <th className="cs-label px-3 py-2 text-center">Ours</th>
              <th className="cs-label px-3 py-2 text-center">Carrier&apos;s class</th>
              <th className="cs-label px-3 py-2 text-right">
                Carrier&apos;s amount
                <span className="normal-case block" style={{ fontWeight: 400, color: 'var(--color-text-dim)', letterSpacing: 0 }}>
                  defaults to ours
                </span>
              </th>
              <th className="cs-label px-3 py-2 text-right">Reclass</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const changed = r.theirClass !== null && r.theirClass !== r.ourClass;
              return (
                <tr key={r.category} style={{ borderTop: '1px solid var(--color-border-faint)', backgroundColor: 'transparent', boxShadow: changed ? 'inset 2px 0 0 var(--color-blocker)' : 'none' }}>
                  <td className="cs-body px-3 py-2" style={{ color: 'var(--color-text-primary)' }}>{r.category}</td>
                  <td className="cs-figure px-3 py-2 text-right" style={{ color: 'var(--color-text-primary)' }}>
                    <span className="inline-flex items-center gap-1.5">
                      {r.ourAmountVerified && (
                        <Check size={11} style={{ color: 'var(--color-verified)' }} aria-label="Verified — from the filed calculation" />
                      )}
                      {formatCurrency(r.ourAmount)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center"><OurClassChip cls={r.ourClass} /></td>
                  <td className="px-3 py-2 text-center">
                    <div
                      role="radiogroup"
                      aria-label={`Carrier's classification for ${r.category}`}
                      className="inline-flex"
                      style={{ border: '1px solid var(--color-border)', borderRadius: 2, overflow: 'hidden' }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); cycleTheirClass(r.category, 1); }
                        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); cycleTheirClass(r.category, -1); }
                      }}
                    >
                      {(['F', 'V', 'P'] as ExpenseClass[]).map((cls) => {
                        const selected = r.theirClass === cls || (r.theirClass === null && r.ourClass === cls);
                        const armed = selected && changed;
                        return (
                          <button
                            key={cls}
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setTheirClass(r.category, cls)}
                            className="cs-chip font-mono"
                            style={{
                              // K3 #4: divergent (armed) pick reads via crimson TEXT, not a crimson fill —
                              // pressed bg is the same neutral for any selected segment.
                              width: 30, padding: '4px 0',
                              backgroundColor: selected ? 'var(--color-bg-hover)' : 'transparent',
                              color: armed ? 'var(--color-blocker)' : selected ? 'var(--color-text-primary)' : 'var(--color-text-dim)',
                              fontWeight: armed ? 600 : 400,
                              borderRight: cls !== 'P' ? '1px solid var(--color-border-sub)' : 'none',
                            }}
                          >
                            {cls}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.theirAmountInput}
                      onChange={(e) => setTheirAmount(r.category, e.target.value)}
                      placeholder={formatCurrency(r.ourAmount)}
                      aria-label={`Carrier's figure for ${r.category} (defaults to ours)`}
                      className="cs-figure text-right"
                      style={{
                        width: 120, padding: '4px 8px', borderRadius: 2,
                        backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)',
                        color: 'var(--color-text-secondary)',
                      }}
                    />
                  </td>
                  <td className="cs-figure px-3 py-2 text-right">
                    {changed ? (
                      <span data-testid={`ccm-flip-${r.ourClass}-${r.theirClass}`} style={{ color: 'var(--color-blocker)' }}>
                        {r.ourClass} → {r.theirClass}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-text-ghost)' }}>·</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-3">
        {/* The section's ONE solid element. */}
        <button
          data-testid="ccm-compute-button"
          onClick={handleCompute}
          disabled={!anyChanged || computing}
          className="cs-body inline-flex items-center gap-2"
          style={{
            padding: '8px 20px', borderRadius: 2, fontWeight: 500,
            backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)',
            opacity: !anyChanged || computing ? 0.5 : 1,
            cursor: !anyChanged || computing ? 'not-allowed' : 'pointer',
          }}
        >
          {computing ? <Loader2 size={14} className="animate-spin" /> : <Swords size={14} />}
          {computing ? 'Computing…' : 'Compute their number'}
        </button>
        {stale && result && (
          <span className="cs-meta inline-flex items-center gap-1.5" style={{ color: 'var(--color-needs-human)' }}>
            <Loader2 size={12} className="animate-spin" /> Overlay edited — figures below are stale until recomputed
          </span>
        )}
      </div>
    </div>
  );

  const renderAttackTable = () => {
    if (!result) return null;
    const vectors = result.attack_vectors; // backend rank order — never re-sort
    return (
      <div data-testid="ccm-attack-table">
        <div className="flex items-center gap-3 mb-2">
          <p className="cs-label" style={{ color: 'var(--color-text-muted)' }}>Attack vectors — ranked by impact</p>
          <span
            className="cs-chip"
            style={{ padding: '2px 8px', borderRadius: 2, backgroundColor: 'transparent', border: '1px solid var(--color-blocker-tint-bd)', color: 'var(--color-blocker)' }}
          >
            THEIR LENS
          </span>
          {result.reconciles === false && (
            <span className="cs-meta" style={{ color: 'var(--color-needs-human)' }}>
              does not foot to line items (floor interaction): table sums to{' '}
              {footedSum < 0 ? `(${formatCurrency(Math.abs(footedSum))})` : formatCurrency(footedSum)}, aggregate gap is{' '}
              {result.delta < 0 ? `(${formatCurrency(Math.abs(result.delta))})` : formatCurrency(result.delta)}
            </span>
          )}
        </div>

        <div style={{ border: '1px solid var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
          {vectors.length === 0 && (
            <p className="cs-body p-4" style={{ color: 'var(--color-text-muted)' }}>
              No attack vectors — the carrier&apos;s map matches ours.
            </p>
          )}
          {vectors.map((v, i) => {
            const w = work[v.category] || emptyWork();
            const hasRebuttal = w.rebuttalText.trim().length > 0;
            const status = STATUS_CONFIG[hasRebuttal ? w.status : 'exposed'];
            const isExpanded = expandedVector === v.category;
            const amountOnly = v.our_class === v.their_class;
            // A flip in OUR favor (V → F concedes more fixed = higher claim) is
            // not an attack — both chips render hueless.
            const concession = v.our_class === 'V' && v.their_class === 'F';
            return (
              <div key={v.category} style={{ borderTop: i > 0 ? '1px solid var(--color-border-faint)' : 'none' }}>
                <button
                  data-testid={`ccm-attack-row-${i}`}
                  onClick={() => setExpandedVector(isExpanded ? null : v.category)}
                  className="w-full flex items-center gap-3 px-4 text-left"
                  style={{ minHeight: 44, backgroundColor: isExpanded ? 'var(--color-bg-elevated)' : 'transparent' }}
                >
                  <span className="cs-figure" style={{ color: 'var(--color-text-dim)', width: 20 }}>{i + 1}</span>
                  <span className="cs-body flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>{v.category}</span>
                  <span className="inline-flex items-center gap-1.5">
                    {amountOnly ? (
                      <span className="cs-figure" style={{ color: 'var(--color-text-muted)' }}>Δ amount</span>
                    ) : (
                      <>
                        <OurClassChip cls={String(v.our_class)} />
                        <span style={{ color: concession ? 'var(--color-text-muted)' : 'var(--color-blocker)' }}>→</span>
                        <TheirClassChip cls={v.their_class} attack={!concession} />
                      </>
                    )}
                  </span>
                  <span className="cs-figure text-right" style={{ width: 120, color: v.loss_impact < 0 ? 'var(--color-blocker)' : 'var(--color-text-secondary)' }}>
                    {v.loss_impact < 0 ? `(${formatCurrency(Math.abs(v.loss_impact))})` : `+${formatCurrency(v.loss_impact)}`}
                  </span>
                  <span className="cs-chip inline-flex items-center gap-1 border" style={{ ...status.style, padding: '2px 8px', borderRadius: 2 }}>
                    {status.icon}
                    {status.label}
                  </span>
                  {isExpanded ? <ChevronUp size={14} style={{ color: 'var(--color-text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--color-text-muted)' }} />}
                </button>

                {isExpanded && (
                  <div className="px-4 py-4 space-y-4" style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                    {/* What they'll argue */}
                    <div>
                      <p className="cs-label mb-1" style={{ color: 'var(--color-text-muted)' }}>What they&apos;ll argue</p>
                      {v.methodology ? (
                        <p className="cs-body">{v.methodology}</p>
                      ) : (
                        <p className="cs-body italic" style={{ color: 'var(--color-text-muted)' }}>
                          No methodology captured yet — transcribe the carrier accountant&apos;s stated rationale here after their report lands.
                        </p>
                      )}
                      <p className="cs-figure mt-1" style={{ fontSize: '0.6875rem', color: 'var(--color-text-dim)' }}>
                        their {v.their_class} treatment: {formatCurrency(v.their_amount)} reclassified into
                        {v.their_class === 'V' ? ' variable costs — shrinks the variable-profit margin' : v.their_class === 'P' ? ' the payroll split — moves dollars inside the VP margin' : ' fixed costs'}
                      </p>
                    </div>

                    {/* Backend-provided rebuttal strategy, if wired */}
                    {v.rebuttal && (
                      <div style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)', borderRadius: 2, padding: 12 }}>
                        <p className="cs-label mb-1" style={{ color: 'var(--color-text-muted)' }}>
                          Engine-suggested defense{v.rebuttal.label ? ` · Strategy ${v.rebuttal.label}` : ''}{v.rebuttal.name ? ` — ${v.rebuttal.name}` : ''}
                        </p>
                        {v.rebuttal.approach && <p className="cs-body">{v.rebuttal.approach}</p>}
                      </div>
                    )}

                    {/* Our rebuttal — the working input */}
                    <div>
                      <p className="cs-label mb-1">Our rebuttal</p>
                      <textarea
                        data-testid={`ccm-rebuttal-input-${i}`}
                        value={w.rebuttalText}
                        onChange={(e) => updateWork(v.category, { rebuttalText: e.target.value })}
                        rows={3}
                        placeholder="How the witness answers this reclassification on the stand…"
                        className="cs-body w-full"
                        style={{
                          padding: '8px 10px', borderRadius: 2, resize: 'vertical',
                          backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)',
                          color: 'var(--color-text-secondary)',
                        }}
                      />
                      {!hasRebuttal && (
                        <p
                          className="cs-body mt-2 px-3 py-2 inline-flex items-center gap-2"
                          style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}
                        >
                          <ShieldAlert size={13} /> No rebuttal prepared — this is an open exposure.
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {(Object.keys(STATUS_CONFIG) as VectorStatus[]).map((s) => {
                          const cfg = STATUS_CONFIG[s];
                          const active = w.status === s;
                          return (
                            <button
                              key={s}
                              onClick={() => updateWork(v.category, { status: s })}
                              aria-pressed={active}
                              className="cs-chip inline-flex items-center gap-1 border"
                              style={{
                                padding: '3px 10px', borderRadius: 2,
                                ...(active ? cfg.style : { color: 'var(--color-text-dim)', borderColor: 'var(--color-border-sub)', backgroundColor: 'transparent' }),
                                opacity: hasRebuttal || s === 'exposed' ? 1 : 0.55,
                              }}
                            >
                              {cfg.icon}
                              {cfg.label}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handleSuggest(v)}
                          disabled={w.suggesting}
                          className="cs-meta inline-flex items-center gap-1.5 ml-auto"
                          style={{
                            padding: '4px 10px', borderRadius: 2,
                            border: '1px solid var(--color-brand)', backgroundColor: 'transparent',
                            color: 'var(--color-brand)',
                          }}
                        >
                          {w.suggesting ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />}
                          Suggest defense strategy
                        </button>
                      </div>
                      {w.suggestError && (
                        <p className="cs-meta mt-2" style={{ color: 'var(--color-blocker)' }}>{w.suggestError}</p>
                      )}
                      {w.suggestions && w.suggestions.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {w.suggestions.map((s, si) => (
                            <div key={si} style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border-sub)', borderRadius: 2, padding: 12 }}>
                              <p className="cs-label mb-1" style={{ color: 'var(--color-text-muted)' }}>
                                {s.label ? `Strategy ${s.label}` : 'Suggested defense'}{s.name ? ` — ${s.name}` : ''}
                              </p>
                              {s.approach && <p className="cs-body mb-2">{s.approach}</p>}
                              {s.counter_arguments && s.counter_arguments.length > 0 && (
                                <ul className="cs-body list-disc list-inside space-y-0.5 mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                                  {s.counter_arguments.map((c, ci) => <li key={ci}>{c}</li>)}
                                </ul>
                              )}
                              {s.evidence_needed && s.evidence_needed.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {s.evidence_needed.map((doc, di) => (
                                    <span key={di} className="cs-meta px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)' }}>
                                      {doc}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <button
                                onClick={() => updateWork(v.category, {
                                  rebuttalText: w.rebuttalText.trim()
                                    ? `${w.rebuttalText.trim()}\n\n${s.approach || ''}`.trim()
                                    : (s.approach || ''),
                                })}
                                className="cs-meta"
                                style={{ padding: '3px 8px', borderRadius: 2, border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', background: 'transparent' }}
                              >
                                Use as rebuttal draft
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWitnessSheet = () => {
    if (!result) return null;
    return (
      <div data-testid="ccm-witness-sheet">
        {renderStatTiles()}
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
          {result.attack_vectors.map((v, i) => {
            const w = work[v.category];
            const hasRebuttal = !!w?.rebuttalText?.trim();
            const status = STATUS_CONFIG[hasRebuttal ? (w?.status || 'exposed') : 'exposed'];
            const firstLine = (w?.rebuttalText || '').split('\n')[0];
            const concession = v.our_class === 'V' && v.their_class === 'F';
            return (
              <div key={v.category} className="flex items-center gap-3 px-4" style={{ minHeight: 40, borderTop: i > 0 ? '1px solid var(--color-border-faint)' : 'none' }}>
                <span className="cs-figure" style={{ color: 'var(--color-text-dim)', width: 20 }}>{i + 1}</span>
                <span className="cs-body" style={{ color: 'var(--color-text-primary)', width: 200 }}>{v.category}</span>
                <span className="cs-figure" style={{ color: concession ? 'var(--color-text-muted)' : 'var(--color-blocker)', width: 70 }}>
                  {v.our_class} → {v.their_class}
                </span>
                <span className="cs-figure text-right" style={{ width: 110, color: v.loss_impact < 0 ? 'var(--color-blocker)' : 'var(--color-text-secondary)' }}>
                  {v.loss_impact < 0 ? `(${formatCurrency(Math.abs(v.loss_impact))})` : `+${formatCurrency(v.loss_impact)}`}
                </span>
                <span className="cs-chip inline-flex items-center gap-1 border" style={{ ...status.style, padding: '2px 8px', borderRadius: 2 }}>
                  {status.icon}{status.label}
                </span>
                <span className="cs-meta flex-1 truncate">
                  {firstLine || <span style={{ color: 'var(--color-blocker)' }}>no rebuttal — open exposure</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // MAIN RENDER — the entire section lives inside one softened crimson frame.
  // K3 #2: the whole panel being adversarial is CONTEXT, not per-element alarm —
  // a solid, low-alpha crimson containment stroke (not dashed marching-ants),
  // 2px radius, single frame (the inner strip divider is a hueless hairline).
  // ---------------------------------------------------------------------------

  const hasRows = rows.length > 0;

  return (
    <section
      data-testid="carrier-counter-model-section"
      className="mb-6"
      style={{
        // Frame is a neutral hairline, not a crimson wash (K3 final-pass ruling
        // 2026-08-24: crimson is for markers only). The sticky HYPOTHETICAL banner
        // below — crimson left-bar + lead word — carries the "this is the carrier's
        // hypothetical, not our filed position" signal on its own.
        border: '1px solid var(--color-hairline)',
        borderRadius: 2,
        backgroundColor: 'var(--color-bg-surface)',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Sticky armor strip — the framing survives scrolling. */}
      <div
        style={{
          position: 'sticky', top: 0, zIndex: 20,
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-sub)',
          borderRadius: '2px 2px 0 0',
          padding: '10px 20px',
        }}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {hypotheticalChip}
          <p className="cs-meta" style={{ margin: 0 }}>
            Models the opposing accountant&apos;s methodology for witness preparation. It never changes our filed calculation.
          </p>
        </div>
      </div>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-[2px]" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <Swords className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
            </div>
            <div>
              <h2 className="cs-h2">Carrier Counter-Model</h2>
              <p className="cs-meta">Reverse-engineer their number. Rebut it before they say it.</p>
            </div>
          </div>
          {expanded && (
            <div className="flex items-center gap-2 flex-wrap">
              {savedAt && (
                <span className="cs-figure inline-flex items-center gap-1" style={{ fontSize: '0.6875rem', color: 'var(--color-verified)' }}>
                  <Check size={11} /> Modeled · {new Date(savedAt).toLocaleString()}
                </span>
              )}
              <button
                onClick={() => setWitnessMode((m) => !m)}
                disabled={!result}
                aria-pressed={witnessMode}
                className="cs-meta inline-flex items-center gap-1.5"
                style={{
                  padding: '5px 10px', borderRadius: 2, border: '1px solid var(--color-border)',
                  backgroundColor: witnessMode ? 'var(--color-bg-elevated)' : 'transparent',
                  color: result ? 'var(--color-text-secondary)' : 'var(--color-text-ghost)',
                }}
              >
                Witness sheet
              </button>
              <button
                onClick={handlePrint}
                disabled={!result}
                className="cs-meta inline-flex items-center gap-1.5"
                style={{ padding: '5px 10px', borderRadius: 2, border: '1px solid var(--color-border)', background: 'transparent', color: result ? 'var(--color-text-secondary)' : 'var(--color-text-ghost)' }}
              >
                <Printer size={12} /> Print
              </button>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Scenario label"
                aria-label="Scenario label"
                title={label || 'Scenario label'}
                className="cs-meta"
                style={{ width: 190, padding: '5px 8px', borderRadius: 2, backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', textOverflow: 'ellipsis' }}
              />
              <button
                data-testid="ccm-save-button"
                onClick={handleSave}
                disabled={saving || !anyChanged}
                className="cs-meta inline-flex items-center gap-1.5"
                style={{
                  // K3 #6: secondary action = ghost, one orange fill per view (that's Compute).
                  padding: '5px 10px', borderRadius: 2, border: '1px solid var(--color-brand)',
                  backgroundColor: 'transparent', color: 'var(--color-brand)',
                  opacity: saving || !anyChanged ? 0.5 : 1,
                }}
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save scenario
              </button>
            </div>
          )}
        </div>

        {error && (
          <p className="cs-body mb-4 px-3 py-2" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-blocker-tint-bd)', borderRadius: 2, color: 'var(--color-blocker)' }}>
            {error}
          </p>
        )}

        {!expanded ? (
          /* Empty / first-run state */
          <div className="text-center py-10">
            <Swords className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <h3 className="cs-body mb-2" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>No carrier model yet</h3>
            <p className="cs-body mb-6 max-w-lg mx-auto">
              Enter the carrier accountant&apos;s fixed/variable classification and see the number their methodology
              produces — plus every reclassification they&apos;ll argue and how to rebut it. Your filed calculation is never changed.
            </p>
            {hasRows ? (
              <button
                data-testid="ccm-open-button"
                onClick={() => setExpanded(true)}
                className="cs-body inline-flex items-center gap-2"
                style={{ padding: '10px 24px', borderRadius: 2, fontWeight: 500, backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <Swords size={15} />
                Model the carrier&apos;s version
              </button>
            ) : (
              <button
                disabled
                className="cs-chip inline-flex items-center gap-2"
                style={{ padding: '10px 24px', borderRadius: 2, border: '1px solid var(--color-border-sub)', color: 'var(--color-text-ghost)', background: 'transparent', opacity: 0.6, cursor: 'not-allowed' }}
              >
                REQUIRES VP ANALYSIS — NOT RUN
              </button>
            )}
          </div>
        ) : witnessMode && result ? (
          renderWitnessSheet()
        ) : (
          <>
            {renderGrid()}
            {result && renderStatTiles()}
            {renderAttackTable()}
            <SavedCostsAttackPanel
              reportId={reportId}
              getAuthHeader={getAuthHeader}
              formatCurrency={formatCurrency}
            />
            <PolicyWaterfallPanel
              reportId={reportId}
              getAuthHeader={getAuthHeader}
              formatCurrency={formatCurrency}
              initialBasis={initialBasis}
            />
            <InsuredPercentagePanel
              reportId={reportId}
              getAuthHeader={getAuthHeader}
              formatCurrency={formatCurrency}
            />
            <BenchmarkPanel
              reportId={reportId}
              getAuthHeader={getAuthHeader}
              formatCurrency={formatCurrency}
            />
          </>
        )}
      </div>
    </section>
  );
}
